import { appendPosthogHandoff, isAppSignupUrl } from '@/lib/auth-url';
import { getPosthogHandoffIds } from '@/lib/posthog';
import { getExternalReferrerHost } from '@/lib/referrer';

/**
 * www → app PostHog hand-off, applied at click time to EVERY signup link.
 *
 * One delegated listener instead of per-CTA wiring: hook-built CTAs, the
 * constant ewidencja hrefs and any future link all pass through the same
 * rewrite, and the ids are read when the visitor acts (PostHog is usually not
 * loaded yet at render time). `click` covers mouse, touch and keyboard
 * activation; `auxclick` covers middle-click "open in new tab". The rewrite
 * runs in the capture phase, before the browser follows the link.
 *
 * `ref_host` is the visitor's external referrer host (www is the app's referrer after the hop, so
 * the original source would otherwise be lost). `ph_sid` is appended in every consent state, `ph_did` only for an accepted
 * visitor (see `getPosthogHandoffIds`). The prerendered href never carries them.
 */
export function rewriteSignupLinkForHandoff(anchor: HTMLAnchorElement): void {
  const href = anchor.href;
  if (!isAppSignupUrl(href)) return;
  const next = appendPosthogHandoff(href, getPosthogHandoffIds(), getExternalReferrerHost());
  if (next !== href) anchor.href = next;
}

function onLinkActivation(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest('a[href]');
  if (anchor instanceof HTMLAnchorElement) rewriteSignupLinkForHandoff(anchor);
}

export function installSignupHandoff(doc: Document = document): () => void {
  doc.addEventListener('click', onLinkActivation, true);
  doc.addEventListener('auxclick', onLinkActivation, true);
  return () => {
    doc.removeEventListener('click', onLinkActivation, true);
    doc.removeEventListener('auxclick', onLinkActivation, true);
  };
}
